'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Field, Form, Icon } from '@plura/client/components';
import { isValidTypeformUrl } from '@plura/shared/lead-forms';
import { Button } from '../../ui/Button';
import Link from 'next/link';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const FormSchema = z.object({
  url: z.string().url('Please enter a valid URL'),
});
type FormData = z.infer<typeof FormSchema>;

interface Props {
  /** Focus this input on mount when the page is opened with `#import` (e.g. from another page's "Import from Typeform"). */
  autoFocusOnImportHash?: boolean;
}

const TypeformConvertForm = ({ autoFocusOnImportHash }: Props) => {
  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      url: '',
    },
  });

  const urlError = form.formState.errors.url?.message;

  // A valid Typeform URL in the field means Convert is one click from doing
  // its job — pulse the button subtly as the nudge.
  const readyToConvert = isValidTypeformUrl(form.watch('url'));

  useEffect(() => {
    if (!autoFocusOnImportHash || typeof window === 'undefined') return;
    if (window.location.hash !== '#import') return;
    const t = window.setTimeout(() => form.setFocus('url'), 400);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocusOnImportHash]);

  // Prefill from ?url= (outreach emails land here carrying the prospect's own
  // Typeform link). Deliberately no auto-submit — the pulsing Convert button
  // leaves the trigger in their hands. window.location instead of
  // useSearchParams keeps the page statically prerenderable.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const prefill = new URLSearchParams(window.location.search).get('url')?.trim();
    if (prefill) form.setValue('url', prefill, { shouldDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (data: FormData) => {
    const isValid = isValidTypeformUrl(data.url);

    if (!isValid) {
      toast.warning("This doesn't look like a Typeform URL", {
        description:
          'Please enter a valid Typeform URL or template URL. Examples: https://form.typeform.com/to/kd92kjdl or https://www.typeform.com/templates/product-recommendation-quiz-template',
      });
      return;
    }

    window.open(`https://app.formsuite.co/typeform-to-formsuite?url=${encodeURIComponent(data.url)}`, '_blank');
  };

  return (
    <div className="mt-5 flex w-full items-start justify-start gap-2.5">
      <div className="flex w-full flex-col items-start justify-start gap-1.25">
        <Form
          form={form}
          autoComplete="off"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="flex w-full flex-col gap-2"
        >
          <div className="relative w-full">
            <Icon
              name="link-1"
              className="pointer-events-none absolute left-4 top-1/2 z-[1] size-5 -translate-y-1/2 text-[#999999] [&_path]:fill-current"
              aria-hidden
            />
            <Field
              id="url"
              placeholder="eg: https://svdkes3fmd4.typeform.com/to/LbWBbXoO"
              classInput="!h-14 !min-h-0 !rounded-full !border !border-[#e5e5e5] !bg-white !pl-12 !pr-36 font-body !text-[15px] !text-dark placeholder:!text-[#999999] focus:!border-green focus:!ring-0 focus:!outline-none"
              error={urlError}
              {...form.register('url')}
            />
            <div className="absolute right-0 top-1/2 z-[2] -translate-y-1/2 p-1">
              <Button type="submit" size="medium" className={readyToConvert ? 'btn-pulse' : undefined}>
                Convert
              </Button>
            </div>
          </div>
        </Form>

        <span className="font-body text-sm font-medium text-[#555555] [&_a]:text-[15px] [&_a]:font-medium [&_a]:text-green [&_a]:underline [&_a:hover]:font-semibold">
          Need help finding your Typeform link?{' '}
          <Link target="_blank" href="/help/articles/import-typeform-to-formsuite">
            Here&apos;s a quick guide
          </Link>
          .
        </span>
      </div>
    </div>
  );
};

export default TypeformConvertForm;
